import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
    MdDashboard, MdSpeed, MdArrowForward,
    MdSchool, MdGroups, MdPayment, MdInventory,
    MdPeople, MdAnalytics, MdNotifications, MdBadge, MdCalendarToday, MdBarChart,
    MdLocationOn, MdTrendingUp, MdVerified, MdSecurity, MdCheckCircle, MdFileDownload
} from 'react-icons/md';

export default function Home() {
    return (
        <div className="bg-white text-gray-900 min-h-screen font-poppins">

            {/* Navigation */}
            <nav className="fixed top-0 w-full bg-white border-b border-gray-200 z-50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <img src={logo} alt="CMS Secure" className="h-8" />
                            <span className="text-xl font-bold text-gray-900">
                                CMS<span className="text-primary-400">SECURE</span>
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <a href="/application-d1eecbd5-93c2-4d78-a29d-2fb24a53d541.apk" download className="inline-flex items-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 bg-green-600 text-white text-xs sm:text-sm font-medium rounded-md hover:bg-green-700 hover:shadow-lg transition-all">
                                <MdFileDownload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span>Download App</span>
                            </a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - Responsive Mobile */}
            <section className="pt-24 sm:pt-28 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-cyan-50 to-white overflow-hidden min-h-auto lg:min-h-[550px] flex items-center">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

                        {/* Left Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary-200 rounded-full mb-6 shadow-sm"
                            >
                                <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                                <span className="text-sm font-semibold text-primary-600">Enterprise Edition v3.0</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                            >
                                All Center Management
                                <br />
                                <span className="text-primary-400">in one place</span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="text-base text-gray-600 leading-relaxed mb-8 max-w-xl"
                            >
                                Complete solution for managing training centers - admissions, fees, payroll, inventory in one secure platform.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="flex flex-col sm:flex-row gap-4"
                            >
                                <a href="/application-d1eecbd5-93c2-4d78-a29d-2fb24a53d541.apk" download className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 hover:shadow-2xl hover:shadow-green-600/50 hover:-translate-y-1 transition-all">
                                    <MdFileDownload className="w-5 h-5" />
                                    Download Android App
                                </a>
                            </motion.div>
                        </motion.div>

                        {/* Right - ANIMATED DASHBOARD CARD */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 50 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="relative mt-8 lg:mt-0"
                        >
                            <motion.div
                                whileHover={{ scale: 1.05, y: -10 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                className="bg-white rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-200"
                            >
                                {/* Browser Header */}
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 text-center text-xs text-gray-400 font-mono">cms-dashboard.local</div>
                                </div>

                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                                    <motion.div
                                        whileHover={{ scale: 1.05, rotate: 2 }}
                                        className="p-3 sm:p-5 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl text-white shadow-lg"
                                    >
                                        <div className="text-2xl sm:text-3xl font-bold">856</div>
                                        <div className="text-xs sm:text-sm mt-1">Total Students</div>
                                        <div className="mt-1 sm:mt-2 flex items-center gap-1 text-xs">
                                            <MdTrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                                            <span>+12%</span>
                                        </div>
                                    </motion.div>
                                    <motion.div
                                        whileHover={{ scale: 1.05, rotate: -2 }}
                                        className="p-3 sm:p-5 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl text-white shadow-lg"
                                    >
                                        <div className="text-2xl sm:text-3xl font-bold">42</div>
                                        <div className="text-xs sm:text-sm mt-1">Active Faculty</div>
                                        <div className="mt-1 sm:mt-2 flex items-center gap-1 text-xs">
                                            <MdVerified className="w-3 h-3 sm:w-4 sm:h-4" />
                                            <span>Verified</span>
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Revenue Chart */}
                                <div className="bg-gray-50 p-3 sm:p-5 rounded-xl border border-gray-200">
                                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                                        <div>
                                            <div className="text-xs text-gray-500">Monthly Revenue</div>
                                            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">₹6.8L</div>
                                        </div>
                                        <motion.div
                                            animate={{ scale: [1, 1.1, 1] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                            className="px-2 sm:px-3 py-1 sm:py-1.5 bg-green-100 text-green-700 text-xs font-bold rounded-full"
                                        >
                                            ↗ +12%
                                        </motion.div>
                                    </div>
                                    <div className="flex items-end gap-1 sm:gap-2 h-16 sm:h-20">
                                        {[40, 65, 45, 80, 70, 60, 85].map((h, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ height: 0 }}
                                                animate={{ height: `${h}%` }}
                                                transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
                                                whileHover={{ height: '100%' }}
                                                className={`flex-1 rounded-t-lg ${i === 6 ? 'bg-gradient-to-t from-primary-400 to-primary-500 shadow-lg' : 'bg-gray-300'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Floating Badges around Dashboard */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
                                transition={{
                                    opacity: { delay: 1.2 },
                                    scale: { delay: 1.2 },
                                    y: { delay: 1.2, duration: 2, repeat: Infinity }
                                }}
                                className="absolute -top-4 -right-4 bg-blue-500 text-white px-4 py-2 rounded-full shadow-xl text-sm font-bold"
                            >
                                Live Data
                            </motion.div>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { number: '2K+', label: 'Active Users', color: 'text-blue-500' },
                            { number: '99.9%', label: 'Uptime', color: 'text-green-500' },
                            { number: '24/7', label: 'Support', color: 'text-purple-500' },
                            { number: '50+', label: 'Centers', color: 'text-orange-500' }
                        ].map((stat, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="text-center p-4 sm:p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-all"
                            >
                                <div className={`text-4xl font-bold ${stat.color} mb-2`}>{stat.number}</div>
                                <div className="text-sm text-gray-600">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid - MORE CURVES */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="text-sm font-bold text-primary-400 mb-2">COMPREHENSIVE FEATURES</div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                            Everything You Need to Run Your Centers
                        </h2>
                        <p className="text-base text-gray-600 max-w-2xl mx-auto">
                            Powerful modules covering every aspect of center operations
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: <MdSchool />, title: 'Student Management', desc: 'Admissions, profiles, ID cards, certificates', gradient: 'from-blue-500 to-blue-600', shadow: 'shadow-blue-500/50' },
                            { icon: <MdPayment />, title: 'Fee Collection', desc: 'Payment gateway, installments, receipts', gradient: 'from-green-500 to-green-600', shadow: 'shadow-green-500/50' },
                            { icon: <MdGroups />, title: 'HR & Payroll', desc: 'Employee management, attendance, salary', gradient: 'from-purple-500 to-purple-600', shadow: 'shadow-purple-500/50' },
                            { icon: <MdInventory />, title: 'Inventory', desc: 'Hardware assets, distribution, tracking', gradient: 'from-orange-500 to-orange-600', shadow: 'shadow-orange-500/50' },
                            { icon: <MdLocationOn />, title: 'Multi-Center', desc: 'Head office & branch management', gradient: 'from-red-500 to-red-600', shadow: 'shadow-red-500/50' },
                            { icon: <MdBarChart />, title: 'Analytics', desc: 'Revenue trends, reports, exports', gradient: 'from-indigo-500 to-indigo-600', shadow: 'shadow-indigo-500/50' },
                            { icon: <MdBadge />, title: 'ID Cards', desc: 'Professional cards with QR codes', gradient: 'from-pink-500 to-pink-600', shadow: 'shadow-pink-500/50' },
                            { icon: <MdCalendarToday />, title: 'Attendance', desc: 'Biometric integration, auto-absent', gradient: 'from-cyan-500 to-cyan-600', shadow: 'shadow-cyan-500/50' },
                            { icon: <MdNotifications />, title: 'Notifications', desc: 'SMS alerts for fees, attendance', gradient: 'from-yellow-500 to-yellow-600', shadow: 'shadow-yellow-500/50' }
                        ].map((feature, idx) => (
                            <FeatureCard3D key={idx} {...feature} delay={idx * 0.1} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose - WITH 3D EFFECTS */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                            Built for Modern Centers
                        </h2>
                        <p className="text-base text-gray-600 max-w-2xl mx-auto">
                            Everything you need to streamline operations
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            { icon: <MdSpeed />, title: 'Lightning Fast', desc: 'Optimized performance with thousands of records', gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-500/50' },
                            { icon: <MdPeople />, title: 'User Friendly', desc: 'Intuitive interface for easy adoption', gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-500/50' },
                            { icon: <MdAnalytics />, title: 'Smart Analytics', desc: 'Real-time insights and data-driven decisions', gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-500/50' }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                whileHover={{ y: -10, scale: 1.02 }}
                                className={`bg-white p-8 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl ${item.shadow} transition-all duration-300`}
                            >
                                <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center text-white text-3xl mb-6 shadow-xl`}>
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-2 text-gray-900">{item.title}</h3>
                                <p className="text-sm text-gray-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Security Section - NEW */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="text-sm font-bold text-primary-400 mb-3">ENTERPRISE SECURITY</div>
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                                Your Data is Safe with Us
                            </h2>
                            <p className="text-base text-gray-600 mb-8">
                                Military-grade encryption, role-based access control, and automated backups ensure your center's data is always protected.
                            </p>
                            <div className="space-y-4">
                                {[
                                    { icon: <MdSecurity />, title: 'AES-256 Encryption', desc: 'Bank-level security for all data' },
                                    { icon: <MdCheckCircle />, title: 'Daily Backups', desc: 'Automatic backups with instant recovery' },
                                    { icon: <MdVerified />, title: 'Compliance Ready', desc: 'GDPR and industry standards' }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="flex gap-4 items-start"
                                    >
                                        <div className="w-12 h-12 bg-primary-400 rounded-2xl flex items-center justify-center text-white text-xl flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                            {[
                                { value: '99.9%', label: 'Uptime' },
                                { value: '<100ms', label: 'Response Time' },
                                { value: '24/7', label: 'Monitoring' },
                                { value: '100%', label: 'Secure' }
                            ].map((stat, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.1 }}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-white p-8 rounded-2xl border border-gray-200 shadow-lg text-center"
                                >
                                    <div className="text-3xl font-bold text-primary-400 mb-2">{stat.value}</div>
                                    <div className="text-sm text-gray-600">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Trusted Stats */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary-400 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-cyan-500 opacity-50"></div>
                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-10">
                        Trusted by Training Centers Nationwide
                    </h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { number: '50+', label: 'Training Centers' },
                            { number: '2K+', label: 'Active Students' },
                            { number: '150+', label: 'Faculty Members' },
                            { number: '₹10Cr+', label: 'Fees Processed' }
                        ].map((stat, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                whileHover={{ scale: 1.1, y: -5 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 shadow-2xl hover:shadow-white/30 transition-all"
                            >
                                <div className="text-4xl font-bold mb-2">{stat.number}</div>
                                <div className="text-sm">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 text-center bg-white">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                        Ready to Transform Your Centers?
                    </h2>
                    <p className="text-base text-gray-600 mb-8">
                        Get started today with enterprise-level center management
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <a href="/application-d1eecbd5-93c2-4d78-a29d-2fb24a53d541.apk" download className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 hover:shadow-2xl hover:-translate-y-1 transition-all shadow-lg">
                            <MdFileDownload className="w-5 h-5" />
                            Download Android App
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer - 2 ROWS ON MOBILE */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                        <div className="col-span-2 lg:col-span-1">
                            <div className="mb-4">
                                <img src={logo} alt="Logo" className="h-10 mb-3" />
                                <div className="text-lg font-bold">
                                    CMS<span className="text-primary-400">SECURE</span>
                                </div>
                            </div>
                            <p className="text-sm text-gray-400">
                                Complete center management solution with enterprise security.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-primary-400 mb-3">Modules</h4>
                            <div className="text-sm text-gray-400 space-y-2">
                                <div className="hover:text-white transition-colors cursor-pointer">Students</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Fees</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Payroll</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Inventory</div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-primary-400 mb-3">Features</h4>
                            <div className="text-sm text-gray-400 space-y-2">
                                <div className="hover:text-white transition-colors cursor-pointer">Attendance</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Analytics</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Reports</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Notifications</div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-primary-400 mb-3">Support</h4>
                            <div className="text-sm text-gray-400 space-y-2">
                                <div className="hover:text-white transition-colors cursor-pointer">24/7 Support</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Documentation</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Training</div>
                                <div className="hover:text-white transition-colors cursor-pointer">Updates</div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-6 text-center text-sm text-gray-500">
                        © 2026 CMS SECURE. All rights reserved.
                    </div>
                </div>
            </footer>

        </div>
    );
}

// 3D Feature Card Component with More Curves - MOBILE CENTER ALIGNED
function FeatureCard3D({ icon, title, desc, gradient, shadow, delay }) {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 30, rotateX: 10 }}
            animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
            transition={{ duration: 0.5, delay }}
            whileHover={{ y: -15, scale: 1.05 }}
            className={`bg-white p-6 rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl ${shadow} transition-all duration-300 cursor-pointer text-center flex flex-col items-center`}
            style={{ transformStyle: 'preserve-3d' }}
        >
            <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className={`w-14 h-14 bg-gradient-to-br ${gradient} text-white rounded-2xl flex items-center justify-center text-2xl mb-4 shadow-xl`}
            >
                {icon}
            </motion.div>
            <h3 className="text-base font-bold mb-2 text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{desc}</p>
        </motion.div>
    );
}
